import { useMemo, useState, type CSSProperties } from "react";
import type { LaneNode, LanesResponse } from "../../api/types";
import { fmtTick } from "../../components/ui";
import { triggerColor, type TriggerColors } from "./triggerColors";

export interface SnapshotWindow {
  trigger: string;
  label: string;
  start: number;
  end: number;
  color?: string;
}

const KIND_COLORS: Record<string, string> = {
  compare: "#60a5fa",
  boolean: "#a78bfa",
  temporal: "#34d399",
  branch: "#fbbf24",
  expr: "#94a3b8",
  state: "#f472b6",
};

export function LanesView({
  lanes,
  total,
  box,
  hoverT,
  pinned,
  selectedNodeId,
  onSelectNode,
  onHoverInterval,
  onPin,
  triggerFilter,
  triggerColors = {},
  windows,
}: {
  lanes: LanesResponse;
  total: number;
  box: { left: number; width: number };
  hoverT: number | null;
  pinned: number | null;
  selectedNodeId: string | null;
  onSelectNode: (n: LaneNode) => void;
  onHoverInterval: (r: [number, number] | null) => void;
  onPin: (t: number) => void;
  triggerFilter: string | null;
  /** Colour per trigger name (shared with the charts); used for the group headings. */
  triggerColors?: TriggerColors;
  /** Captured snapshot windows; drawn as bars in the top lane. */
  windows: SnapshotWindow[];
}) {
  const [showEmpty, setShowEmpty] = useState(false);
  const groups = useMemo(() => {
    // A boolean node nobody else consumes is the trigger's final condition -- the lane that
    // answers "does this second qualify?". Shown last and called out as the result.
    const consumed = new Set<string>();
    for (const n of lanes.nodes) for (const i of n.inputs ?? []) consumed.add(i);
    const byTrigger = new Map<string, { n: LaneNode; root: boolean }[]>();
    for (const n of lanes.nodes) {
      if (!n.bool) continue;
      if (triggerFilter && n.trigger !== triggerFilter) continue;
      if (!showEmpty && !(n.true?.length)) continue;
      const arr = byTrigger.get(n.trigger) ?? [];
      arr.push({ n, root: !consumed.has(n.id) });
      byTrigger.set(n.trigger, arr);
    }
    for (const arr of byTrigger.values()) arr.sort((a, b) => Number(a.root) - Number(b.root));
    return [...byTrigger.entries()];
  }, [lanes, triggerFilter, showEmpty]);

  const pct = (t: number) => `${(Math.min(Math.max(t, 0), total) / Math.max(total, 1)) * 100}%`;
  const shownWindows = windows.filter((w) => !triggerFilter || w.trigger === triggerFilter);

  return (
    <div className="lanes">
      <div className="lanes-head row small dim">
        <span style={{ width: box.left, paddingLeft: 6 }}>lanes</span>
        <span>{lanes.mode === "bool" ? "boolean sub-conditions" : lanes.mode} · click a bar to pin, click a label to inspect the node</span>
        <span className="spacer" />
        <label className="row" style={{ gap: 4 }}>
          <input type="checkbox" checked={showEmpty} onChange={(e) => setShowEmpty(e.target.checked)} /> show never-true
        </label>
      </div>
      <div className="lane snapshots">
        <div className="lane-label" style={{ width: box.left }}>
          snapshots
        </div>
        <div className="lane-track" style={{ width: box.width }} onClick={(e) => onPin(trackT(e, total))}>
          {shownWindows.map((w, i) => (
            <span
              key={i}
              className="snap-window"
              style={{ left: pct(w.start), width: `max(3px, ${pct(w.end - w.start + 1)})`, "--snap": w.color ?? triggerColor(w.trigger, triggerColors) } as CSSProperties}
              title={`${w.trigger}/${w.label}: ${fmtTick(w.start)} → ${fmtTick(w.end)} (${w.end - w.start + 1}s)`}
              onMouseEnter={() => onHoverInterval([w.start, w.end])}
              onMouseLeave={() => onHoverInterval(null)}
              onClick={(e) => (e.stopPropagation(), onPin(w.start))}
            />
          ))}
          <Cursor hoverT={hoverT} pinned={pinned} pct={pct} />
        </div>
      </div>
      {groups.map(([trigger, nodes]) => (
        <div key={trigger} className="lane-group">
          <div className="lane-group-title small" style={{ paddingLeft: 6 }}>
            <span style={{ color: triggerColor(trigger, triggerColors) }}>⚡</span> {trigger}
          </div>
          {nodes.map(({ n, root }) => (
            <div key={n.id} className={`lane${n.id === selectedNodeId ? " sel" : ""}${root ? " root" : ""}`}>
              {root ? (
                <div className="lane-result-head small" style={{ paddingLeft: 10 }} title={`${n.kind} · ${n.file}:${n.line}`} onClick={() => onSelectNode(n)}>
                  <span className="result-tag" style={{ color: triggerColor(trigger, triggerColors) }}>⇒ result of {trigger}</span>
                  <span className="dim">final condition, combined from the lanes above — the seconds the trigger acts on:</span>
                </div>
              ) : null}
              <div
                className="lane-label mono"
                style={{ width: box.left }}
                title={`${n.kind} · ${n.file}:${n.line}\n${n.label}${root ? "\n\nFinal condition of this trigger (not used by any other condition)." : ""}`}
                onClick={() => onSelectNode(n)}
              >
                <span className="dot" style={{ background: root ? triggerColor(trigger, triggerColors) : KIND_COLORS[n.kind] ?? "#94a3b8" }} />
                {n.label}
              </div>
              <div className="lane-track" style={{ width: box.width }} onClick={(e) => onPin(trackT(e, total))}>
                {(n.evaluated ?? []).map(([a, b], i) => (
                  <span key={`e${i}`} className="seg eval" style={{ left: pct(a), width: pct(b - a) }} />
                ))}
                {(n.true ?? []).map(([a, b], i) => (
                  <span
                    key={i}
                    className="seg on"
                    style={{ left: pct(a), width: `max(2px, ${pct(b - a)})`, background: root ? triggerColor(trigger, triggerColors) : KIND_COLORS[n.kind] ?? "#94a3b8" }}
                    title={`${fmtTick(a)} → ${fmtTick(b)} (${b - a}s)`}
                    onMouseEnter={() => onHoverInterval([a, b])}
                    onMouseLeave={() => onHoverInterval(null)}
                  />
                ))}
                <Cursor hoverT={hoverT} pinned={pinned} pct={pct} />
              </div>
            </div>
          ))}
        </div>
      ))}
      {!groups.length ? (
        <div className="dim small" style={{ padding: 8 }}>
          No boolean lanes {triggerFilter ? `for ${triggerFilter}` : ""}. Run with tracing enabled (trace=bool) or tick "show never-true".
        </div>
      ) : null}
    </div>
  );
}

function Cursor({ hoverT, pinned, pct }: { hoverT: number | null; pinned: number | null; pct: (t: number) => string }) {
  return (
    <>
      {hoverT != null ? <span className="cursor hover" style={{ left: pct(hoverT) }} /> : null}
      {pinned != null ? <span className="cursor pin" style={{ left: pct(pinned) }} /> : null}
    </>
  );
}

function trackT(e: React.MouseEvent<HTMLDivElement>, total: number): number {
  const rect = e.currentTarget.getBoundingClientRect();
  return Math.round(((e.clientX - rect.left) / rect.width) * total);
}
